import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { ResidentInfo } from './ResidentInfo';
import type { Room, PaymentPeriod, Resident } from '../types';
import type { User } from '../types/auth';

interface RoomCardProps {
  room: Room;
  currentUser: User;
  onToggleExpand: () => void;
  onDeleteRoom: () => void;
  onPaymentPatternChange: (roomNumber: string, residentPosition: number, pattern: number) => void;
  onPaymentPeriodChange: (
    roomNumber: string,
    residentPosition: number,
    periodId: string,
    updates: Partial<PaymentPeriod>
  ) => void;
  onAddPaymentPeriod: (roomNumber: string, residentPosition: number) => void;
  onResidentInfoChange: (roomNumber: string, residentPosition: number, updates: Partial<Resident>) => void;
  onDeletePaymentPeriod: (paymentPeriodId: string) => Promise<boolean>;
  onDeleteResident: (residentId: string) => Promise<boolean>;
  onEditResident: (resident: Resident) => void;
  onBackupResident?: (residentId: string, reason?: string) => Promise<void>;
}

export function RoomCard({
  room,
  currentUser,
  onToggleExpand,
  onDeleteRoom,
  onDeletePaymentPeriod,
  onDeleteResident,
  onEditResident,
  onBackupResident
}: RoomCardProps) {
  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center border-b-2 border-light-border dark:border-dark-border">
        <button
          onClick={onToggleExpand}
          className="flex-1 p-3 sm:p-5 flex justify-between items-center hover:bg-light-surfaceAlt dark:hover:bg-dark-surfaceAlt transition-colors"
        >
          <h3 className="text-sm sm:text-xl font-bold text-primary-700 dark:text-primary-400">
            Kamar {room.number}
          </h3>
          <div className="flex items-center gap-1 sm:gap-3">
            {room.residents.length > 0 && (
              <span className="badge badge-info text-xs sm:text-sm px-2 py-1 sm:px-4 sm:py-2">
                {room.residents.length}
              </span>
            )}
            {room.isExpanded ? (
              <ChevronUp className="h-4 w-4 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-400" />
            ) : (
              <ChevronDown className="h-4 w-4 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-400" />
            )}
          </div>
        </button>
        {currentUser.accessLevel === 1 && (
          <button
            onClick={onDeleteRoom}
            className="p-3 sm:p-5 text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/30 transition-colors border-l-2 border-light-border dark:border-dark-border"
            title="Hapus kamar"
          >
            <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        )}
      </div>

      {/* Content */}
      {room.isExpanded && (
        <div className="p-3 sm:p-5 bg-light-surfaceAlt dark:bg-dark-surfaceAlt space-y-3 sm:space-y-4">
          {[1, 2].map((position) => {
            const resident = room.residents.find(r => r.position === position);
            return (
              <ResidentInfo
                key={position}
                position={position as 1 | 2}
                resident={resident}
                currentUser={currentUser}
                onDeletePaymentPeriod={onDeletePaymentPeriod}
                onDeleteResident={onDeleteResident}
                onEditResident={onEditResident}
                onBackupResident={onBackupResident}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
